/** Pure helpers for Renewals due (0087) - kept free of React so the tests can load them. */

export interface RenewalRow {
  id: string;
  kind: string; kind_label: string; entity_table: string; record_id: string; record_code: string | null; what: string;
  subject: string | null; phone: string | null; village_name: string | null; site_name: string | null;
  due_on: string; days_left: number; due_state: 'expired' | 'due_30' | 'due_90' | 'later'; nav_key: string;
  renewal_in_progress: boolean;
}

/** Kinds whose own page finds the record by its code; the rest are found by who it is for. */
const SEARCH_BY_CODE = new Set(['chamber_maintenance', 'contractor_licence', 'site_licence', 'plot_lease', 'harvest_permit', 'accident_action', 'complaint_response', 'certificate']);

export function openPath(row: Pick<RenewalRow, 'kind' | 'nav_key' | 'record_code' | 'subject'>): string {
  const q = SEARCH_BY_CODE.has(row.kind) ? row.record_code : row.kind === 'audit_action' ? null : row.subject;
  return `/${row.nav_key}${q ? `?q=${encodeURIComponent(q)}` : ''}`;
}

/** Consents and KYC renew in one click: their own page opens a new record filled in from this one. */
export function renewPath(row: Pick<RenewalRow, 'kind' | 'record_id'>): string | null {
  if (row.kind === 'consent') return `/consent-capture?renew=${row.record_id}`;
  if (row.kind === 'kyc') return `/kyc?renew=${row.record_id}`;
  return null;
}

/** A renewal starts the day after the old one ends - or today, if it has already lapsed. */
export function renewalStart(validTo: string | null | undefined, today: string): string {
  if (!validTo || validTo < today) return today;
  // Built from the date parts and read back the same way: toISOString() would shift the day
  // backwards anywhere east of UTC, so a renewal would start on the day the old one ends.
  const [y, m, d] = validTo.split('-').map(Number);
  const date = new Date(y!, m! - 1, d! + 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** "3 days ago", "today", "in 12 days". */
export function dueWords(days: number): string {
  if (days === 0) return 'today';
  if (days < 0) return `${-days} day${days === -1 ? '' : 's'} ago`;
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

/** A WhatsApp chat with the reminder typed in; the officer presses send. Null without a number. */
export function reminderLink(row: Pick<RenewalRow, 'phone' | 'what' | 'due_on' | 'days_left' | 'subject'>): string | null {
  const digits = (row.phone ?? '').replace(/\D/g, '');
  if (digits.length < 10) return null;
  const number = digits.length === 10 ? `91${digits}` : digits;
  const when = row.days_left < 0 ? `expired on ${row.due_on}` : `is due for renewal on ${row.due_on}`;
  const text = `Namaste${row.subject ? ` ${row.subject}` : ''}, your ${row.what} ${when}. Please contact your field officer to renew it.`;
  return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
}
